import { FC, useEffect } from 'react';
import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { colors } from 'shared/styles/colors';
import { CloseModalIcon } from 'shared/ui/Modal/icons';

interface ModalProps {
  children: React.ReactNode;
  onClose: () => void;
  title?: string;
  isOpen?: boolean;
  customDimensions?: {
    width?: string;
    height?: string;
    maxWidth?: string;
    minHeight?: string;
  };
}

export const ModalOverlay = styled.div`
    ${tw`fixed inset-0 flex items-center justify-center z-50`};
    backdrop-filter: blur(34px);
    background: rgba(23, 47, 114, 0.4);
    padding: 16px;
    
    @media (max-width: 768px) {
        padding: 0;
    }
`;

export const ModalContainer = styled.div<{ $customDimensions?: any }>`
    box-shadow: 0px 18px 36px -18px rgba(0, 0, 0, 0.05);
    border-radius: 12px;
    background: ${colors.accentWhite};
    padding: 24px;
    display: flex;
    flex-direction: column;
    margin-top: 100px;
    
    /* Default dimensions */
    width: 100%;
    max-width: ${props => props.$customDimensions?.maxWidth || '1200px'};
    height: ${props => props.$customDimensions?.height || '80vh'};
    
    /* Apply custom dimensions if provided */
    ${props => props.$customDimensions?.width && `width: ${props.$customDimensions.width};`}
    ${props => props.$customDimensions?.minHeight && `min-height: ${props.$customDimensions.minHeight};`}
    
    @media (max-width: 768px) {
        width: 100%;
        margin-top: auto;
        height: calc(100% - 80px);
        padding: 20px;
    }
`;

const ModalHeader = styled.div`
    ${tw`flex justify-end items-end`};
    flex-shrink: 0;
`;

const ModalContent = styled.div`
    ${tw`flex-1`}
    overflow-y: auto;
    max-height: 100%;
`;

export const Modal: FC<ModalProps> = ({
  children, 
  onClose, 
  title, 
  isOpen = true, 
  customDimensions
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer 
        onClick={(e) => e.stopPropagation()}
        $customDimensions={customDimensions}
      >
        <ModalHeader>
          {title && <h2 className="text-2xl font-semibold text-primary pr-4">{title}</h2>}
          <span className="cursor-pointer" onClick={onClose}>
            <CloseModalIcon/>
          </span>
        </ModalHeader>
        
        <ModalContent>
          {children}
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};